import { FeatureFlag } from '@revelio/core';

import { useUnleashFlag } from '../../shared/unleash-flags/use-unleash-flag';
import {
  AISearchFilterBar,
  FilterBar,
  ScreenerV2FilterBar,
} from './components/FilterBar/FilterBar';
import { Header } from './components/Header';
import {
  usePageConfig,
  useTabContentConfig,
} from './components/PageConfigProvider/PageConfigProvider';
import {
  PAGES_WITH_AI_SEARCH,
  PageType,
  RevelioPageId,
} from './components/PageConfigProvider/types';
import { FilterId } from './filters/types/filters';
import { isScreenerV2Page } from './utils/routing-helpers';

const AI_SEARCH_PRIMARY_FILTER_IDS = [
  FilterId.COMPANY,
  FilterId.GEOGRAPHY,
  FilterId.ROLE,
] as const;

export const CustomDashPageSpecificWrappers = ({
  children,
}: React.PropsWithChildren) => {
  const { config } = usePageConfig();
  const { config: tabContentConfig } = useTabContentConfig();
  const revelioPageId = config.revelioPageId;
  const hasRevelioPageId = Boolean(revelioPageId);
  const isReportPage = config.type === PageType.REPORT;
  const isScreenerPage = isScreenerV2Page(revelioPageId);
  const isAiSearchWiderDashboardEnabled = useUnleashFlag(
    FeatureFlag.AiSearchWiderDashboard
  );

  // Report Page
  if (isReportPage) {
    return (
      <>
        <Header isReportPage={true} />
        {children}
      </>
    );
  }

  // Determine if AI search should be shown for this page
  // Talent Discovery and screener V2 pages always have AI search; other revelio pages require the feature flag
  // Custom dash private pages (company/geography/role primary filter) also get AI search
  const isAiSearchPage = revelioPageId
    ? PAGES_WITH_AI_SEARCH.includes(revelioPageId)
    : false;
  const isPrivatePageWithAiSearchPrimaryFilter =
    config.type === PageType.PRIVATE &&
    tabContentConfig.primaryFilter &&
    AI_SEARCH_PRIMARY_FILTER_IDS.includes(tabContentConfig.primaryFilter.id);
  const shouldShowAiSearch =
    isPrivatePageWithAiSearchPrimaryFilter ||
    (hasRevelioPageId &&
      (revelioPageId === RevelioPageId.TALENT_DISCOVERY ||
        isScreenerPage ||
        (isAiSearchWiderDashboardEnabled && isAiSearchPage)));

  const filterBar = (() => {
    if (shouldShowAiSearch) {
      return <AISearchFilterBar hidePrimaryFilterBar={isScreenerPage} />;
    }
    if (isScreenerPage) {
      return <ScreenerV2FilterBar />;
    }

    return <FilterBar />;
  })();

  return (
    <>
      <Header isReportPage={false} />
      {filterBar}
      {children}
    </>
  );
};
